import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Play, Info, Star } from "lucide-react";

export const HeroCarousel = ({ movies }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % movies.length);
  }, [movies.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + movies.length) % movies.length);
  }, [movies.length]);

  useEffect(() => {
    if (!isAutoPlaying || movies.length === 0) return;
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide, movies.length]);

  if (!movies || movies.length === 0) {
    return (
      <div className="relative w-full h-[70vh] bg-[#171717] flex items-center justify-center">
        <p className="text-gray-400">Aucun film disponible</p>
      </div>
    );
  }

  const currentMovie = movies[currentIndex];
  const backdropUrl = currentMovie?.backdrop_path || currentMovie?.poster_path || "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1920";

  return (
    <div
      className="relative w-full h-[70vh] md:h-[80vh] overflow-hidden"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
      data-testid="hero-carousel"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={backdropUrl}
          alt={currentMovie?.title}
          className="w-full h-full object-cover transition-opacity duration-700"
          key={currentMovie?.movie_id}
        />
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/80 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="absolute inset-0 flex items-end">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 md:pb-32">
          <div className="max-w-2xl animate-slide-up" key={currentMovie?.movie_id}>
            {/* Rating */}
            {currentMovie?.vote_average > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/30">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-yellow-400 font-semibold">
                    {currentMovie.vote_average.toFixed(1)}
                  </span>
                </div>
                {currentMovie?.release_date && (
                  <span className="text-gray-400 text-sm">
                    {currentMovie.release_date.split("-")[0]}
                  </span>
                )}
              </div>
            )}

            {/* Title */}
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight">
              {currentMovie?.title}
            </h1>

            {/* Genres */}
            {currentMovie?.genres?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {currentMovie.genres.slice(0, 4).map((genre, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 text-sm rounded-full bg-white/10 text-white border border-white/10"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            )}

            {/* Overview */}
            <p className="text-gray-300 text-sm md:text-base line-clamp-3 mb-6 leading-relaxed">
              {currentMovie?.overview || "Aucune description disponible."}
            </p>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <Link to={`/movie/${currentMovie?.movie_id}`}>
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white font-semibold px-8 rounded-full"
                  data-testid="hero-play-button"
                >
                  <Play className="w-5 h-5 mr-2 fill-current" />
                  Voir le film
                </Button>
              </Link>
              <Link to={`/movie/${currentMovie?.movie_id}`}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 bg-white/5 hover:bg-white/10 text-white font-semibold px-8 rounded-full"
                  data-testid="hero-info-button"
                >
                  <Info className="w-5 h-5 mr-2" />
                  Plus d'infos
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {movies.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all duration-200 hover:scale-110"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all duration-200 hover:scale-110"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {movies.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
          {movies.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                idx === currentIndex
                  ? "w-8 bg-violet-500"
                  : "bg-white/30 hover:bg-white/50"
              }`}
              data-testid={`carousel-dot-${idx}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
